import { APICompsSchema, APIOps } from "@/apis/api-config";
import { AppCalendarEventType } from "@/utils/app-types";
import { formatJpDate } from "@/utils/time-utils";

import { addDays, addMonths } from "date-fns";
import { atom } from "jotai";
import { PrimitiveAtom } from "jotai/core/atom";

export const initializingAtom = atom<boolean>(true);
export const loadingAtom = atom<boolean>(false);
export const reportLoadingAtom = atom<string | null>(null) as PrimitiveAtom<string | null>;
export const signupLoadingAtom = atom<string | null>(null) as PrimitiveAtom<string | null>;
export const idTokenAtom = atom<string | null>(null) as PrimitiveAtom<string | null>;

export const authUserAtom = atom<APICompsSchema["MemberResponse"] | null>(null) as PrimitiveAtom<
  APICompsSchema["MemberResponse"] | null
>;
export const instagramAccountInfoAtom = atom<APICompsSchema["InstagramAccountWithSubscriptionSchema"] | null>(
  null,
) as PrimitiveAtom<APICompsSchema["InstagramAccountWithSubscriptionSchema"] | null>;
// 法人ダッシュボードの所属組織。1ユーザーが複数組織に所属しうるので一覧で持ち、
// 選択中の組織 ID を別に持って派生 atom で合成する。
export const corpMembershipsAtom = atom<APICompsSchema["OrganizationMembershipSchema"][]>([]);
export const selectedOrganizationIdAtom = atom<string | null>(null) as PrimitiveAtom<string | null>;

/** 選択中の所属(組織 + 役割)。選択が未設定・不正なときは先頭にフォールバックする */
export const currentMembershipAtom = atom((get) => {
  const list = get(corpMembershipsAtom);
  const id = get(selectedOrganizationIdAtom);
  return list.find((m) => m.organization_id === id) ?? list[0] ?? null;
});

/** 選択中の組織。読み取り専用の派生 atom(書き込みは selectedOrganizationIdAtom 側で行う) */
export const organizationAtom = atom((get) => {
  const membership = get(currentMembershipAtom);
  if (!membership) return null;
  return {
    id: membership.organization_id,
    company_name: membership.company_name,
    company_url: membership.company_url ?? null,
  };
});

// HINOMEエンタープライズのデモモード。デモアカウントでログインした場合だけ true になる
export const corporateDemoAtom = atom<boolean>(false);

export const refererAtom = atom<string>("");
export const pageTitleAtom = atom<string>("");
export const goBackPathAtom = atom<string>("/");
export const dateRangeAtom = atom<[Date, Date]>([addDays(addMonths(new Date(), -1), -1), addDays(new Date(), -1)]);
// 広告管理専用の日付範囲Atom（デフォルト: 過去30日）
export const adsDateRangeAtom = atom<[Date, Date]>([addDays(new Date(), -30), new Date()]);
// 投稿コンテンツ配下(投稿一覧のフィード・リール・ストーリーズ、24時間分析のフィード・リール)で共有する期間。
// ページを移っても同じ期間のまま見られるよう、useState ではなく atom で持つ
export const postContentDateRangeAtom = atom<[Date, Date]>([addDays(new Date(), -31), new Date()]);
// 広告管理: 選択中のアカウントID（ページ間で共有）
export const selectedAdAccountIdAtom = atom<string>("");
export const facebookPermissionsAtom = atom<Array<string>>([]);

export const contentsMediaLabelsAtom = atom<APICompsSchema["MediaLabelsSchema"][]>([]);
/** エンタープライズのコンテンツ検索（5 ページ共通）で選んでいるアカウントラベル。ページを移っても残す */
export const corpContentSelectedLabelsAtom = atom<APICompsSchema["InstagramAccountLabelsSchema"][]>([]);
export const corpInstagramLabelsAtom = atom<APICompsSchema["InstagramAccountLabelsSchema"][]>([]);

export const croppedImgSrcAtom = atom<string>("");

export const selectedMediaAtom = atom<APICompsSchema["GetMediaContentsResponse"][]>([]);
export const selectedScheduledMediaAtom = atom<APICompsSchema["GetScheduledPostsResponse"] | null>(
  null,
) as PrimitiveAtom<APICompsSchema["GetScheduledPostsResponse"] | null>;

export const calendarCurrentEventsAtom = atom<AppCalendarEventType[]>([]);

// for admin dialog
export const forAdminSetPlanDialogAtom = atom<{
  open: boolean;
  obj: APICompsSchema["InstagramAccountWithSubscriptionSchema"] | null;
}>({
  open: false,
  obj: null,
});

export const forAdminSetInstagramTableContainerAtom = atom<
  APIOps["get_admin_instagram_table_api_admin_instagram_table_get"]["parameters"]["query"]
>({
  order_by: "created_at",
  order_by_asc_desc: "desc",
});

export const forAdminSetUserTableContainerAtom = atom<
  APIOps["get_admin_user_table_api_admin_user_table_get"]["parameters"]["query"]
>({
  order_by: "created_at",
  order_by_asc_desc: "desc",
});

// for corporate dialog
// 法人ダッシュボードのInstagramアカウント一覧の初期フィルタ
// デフォルトは全期間のアカウントを表示（created_at_start/endを未指定）
// 一覧の絞り込みは画面側で行う（API には送らない）ので、条件の形はここで持つ
export type CorpInstagramTableFilterParams = {
  over_follower_count?: number;
  under_follower_count?: number;
  over_follow_count?: number;
  under_follow_count?: number;
  over_post_count?: number;
  under_post_count?: number;
  over_benchmark_count?: number;
  under_benchmark_count?: number;
  over_reach_count?: number;
  under_reach_count?: number;
  over_profile_views_count?: number;
  under_profile_views_count?: number;
  over_website_clicks_count?: number;
  under_website_clicks_count?: number;
  created_at_start?: string;
  created_at_end?: string;
  keyword?: string;
  label_ids?: string;
  order_by?: string;
  order_by_asc_desc?: string;
};
export const forCorpSetInstagramTableContainerAtom = atom<CorpInstagramTableFilterParams>({
  order_by: "created_at",
  order_by_asc_desc: "desc",
});

export const selectedInstagramAccountIdsAtom = atom<string[]>([]);

// コメント管理: 選択中の投稿情報
export type SelectedCommentMedia = {
  mediaId: string;
  mediaUrl: string;
  mediaProductType: string;
  timestamp: string;
  likeCount: number;
  commentCount: number;
  caption: string;
  permalink: string;
  reachCount: number | null;
  impressionCount: number | null;
  savedCount: number | null;
  shareCount: number | null;
  engagementCount: number | null;
  playCount: number | null;
};
export const selectedCommentMediaAtom = atom<SelectedCommentMedia | null>(
  null,
) as PrimitiveAtom<SelectedCommentMedia | null>;
